import { atomWithStorage } from "jotai/utils";
import { Socket } from "socket.io-client";
import { atom } from "jotai";

import GCLogo from "../assets/img/growthcloud/growthcloud_wordmark.svg";
import { Module, Schema, TenantInfo } from "../types/TenantInterfaces";
import { BriePages } from "../components/BrieChatMenu/BrieChatMenu";
import { UserRecordActivity } from "../types/GlobalInterfaces";
import FieldUtils from "../utils/FieldUtils";

interface RecordLayouts {
  [moduleAPIName: string]: {
    [layoutID: string]: string[]; // an array or record ids.
  };
}

// For Query Builder Drawer.
export const queryBuilderDrawer = atom<boolean>(false);
// To know if the schema has been updated (only for the query builder).
export const queryBuilderUpdatedSchema = atom<boolean>(false);

// Stores Socket IO socket
export const socketAtom = atom<Socket | null>(null);

// Stores boolean that enables/disables React Query hooks.
export const queriesEnabledAtom = atom(false);

// Stores the info of the cloud you are currently in.
export const tenantAtom = atom<TenantInfo | null>(null);

// ReadOnly Atom that stores the id of the cloud you are currently in. Reacts to tenantAtom.
export const tenantIDAtom = atom<string | null>(
  (get) => get(tenantAtom)?.TenantID || null
);

export const isGlobalTenantAtom = atom<boolean>((get) =>
  get(tenantAtom) ? get(tenantAtom)?.GlobalAppBuilder ?? false : false
);

// DO NOT export this atom. Use schemaAtom and setSchemaAtom instead
const globalSchemaAtom = atom<Schema | null>(null);

// Stores the current schema of the cloud you are currently in.
export const schemaAtom = atom<Schema | null>((get) => get(globalSchemaAtom));

// Write only atom for setting the schemaAtom
export const setSchemaAtom = atom(null, (_, set, newSchema: Schema | null) => {
  FieldUtils.addDefaultsToSchema(newSchema);
  set(globalSchemaAtom, newSchema);
});

// ReadOnly Atom that stores the modules of the current schema. Reacts to schemaAtom.
export const modulesAtom = atom<Array<Module> | null>((get) =>
  get(schemaAtom) ? get(schemaAtom)?.CRM?.Modules ?? null : null
);
// The number of Drawers open
export const recordDetailDrawerOpenCount = atom(0);

// Stores boolean to show MultiReference component when oppening the ViewRecordDrawer
export const recordViewAtom = atom(false);

// Stores boolean to show ObjectCreateDrawer when opening in the advanced filter
export const objectFilter = atom(false);

// Stores boolean to show RestoringBackupModal
export const restoringBackupAtom = atom(false);

// Stores boolean that indicates if selectableInput component is open in AdvancedFilter
export const selectableOpen = atom(false);

export const briePageAtom = atom<BriePages>("home");

// ConversationID inside brie
export const conversationIDAtom = atom("new");

export const currentCloudDataAtom = atom<any>(null);

// Stores whether or not the current cloud is global
export const isGlobalCloudAtom = atom<boolean>((get) =>
  get(currentCloudDataAtom) ? get(currentCloudDataAtom)?.Global ?? false : false
);

// Stores whether or not the current cloud has brie disabled
export const isBrieDisabledAtom = atom<boolean>((get) =>
  get(currentCloudDataAtom)
    ? get(currentCloudDataAtom)?.BrieDisabled ?? true
    : true
);

// Stores current cloud related records
export const cloudRelatedRecordsAtom = atom<any[]>((get) =>
  get(currentCloudDataAtom)
    ? get(currentCloudDataAtom)?.RelatedRecords ?? []
    : []
);

export const recordLayouts = atom<RecordLayouts>({});

// For Cloud Onboarding
export const addUserModal = atom<boolean>(false);

// All of the drawers that have been opened, tracked by View ID
// Each View can have one Drawer open - SubViews use their id as well.
export const drawersInURLAtom = atom<string[]>([]);

// Whether or not Cards are shrunk in Board/Timeline views.
// Persists to user's preference
export const shrinkCardsAtom = atomWithStorage("shrinkCards", false);

// Maintenance mode
export const maintenanceModeAtom = atom({
  Status: false,
  Title: "",
  Description: "",
  EndDateTime: "",
});

/** Tenant Cloud Logo and Theme.  */

// Stores cloud logo.
export const cloudLogoAtom = atomWithStorage("cloudLogo", GCLogo);
export const cloudLogo = atom<string>((get) =>
  get(cloudLogoAtom) ? get(cloudLogoAtom) ?? GCLogo : GCLogo
);

// Stores the cloud theme.
export const cloudColorTheme = atom<string>((get) =>
  get(currentCloudDataAtom)
    ? get(currentCloudDataAtom)?.ColorTheme ?? "#ffff"
    : "#ffff"
);

/**
 * User Record Activity
 */
export const userRecordActivityAtom = atomWithStorage<
  UserRecordActivity | undefined
>("userRecordActivityAtom", undefined);

/**
 * AI Model
 */
export const modelAtom = atomWithStorage("brie-gpt-model", "gemini-2-0-flash");
